// 世界史スポットの上空に浮かべる「くぐる輪」の描画と通過判定。
//
// 向きの方針:
//  - リングは縦置きのトーラス（TorusGeometry は XY 平面に輪、穴の軸がローカル +Z）で、
//    Y軸まわりに機体の方を向く。追従は指数イージングなので、遠方の輪はゆっくり向きを
//    合わせ、至近距離では方位変化率が大きすぎて追従しきれず「その場に静止した門」に
//    なる。状態機械なしで「遠くからは必ず正面／通過時は固定」が両立する。
//  - どの方位から近づいても必ずくぐれるので、教材としてストレスがない。
//
// 通過判定:
//  - 常に機体を向く輪が見せうる開口部の和集合は、中心からの半径 r の球そのものになる
//    （任意の点 P は、鉛直と P 方向が張る平面＝水平法線をもつ向きの開口に含まれる）。
//    したがって「中心からの3D距離 < r」は近似ではなく厳密な判定で、ビルボードの
//    追従遅れと絶対にズレない。
//  - 1フレームの移動を線分として扱い、線分と球の最短距離で判定する（すり抜け防止）。
//  - 一度入った輪は insideIds に記録し、3r 離れるまで再発火しない。ポーズ中も
//    この集合は保たれるので、モーダルを閉じた瞬間に再発火しない。
//
// 描画の注意:
//  - シーンには近傍のリングだけを入れる。fog は 8,000〜40,000m、camera.far は 50,000m。
//  - マテリアルは fog:false（霧に溶けると目印にならない）。代わりに距離フェードを自前で
//    かけて、遠方でいきなり満輝度でポップインしないようにする。
//  - リングは scene に直接足す。tiles.group に入れると game.js のレイキャスト
//    （clampAircraftToGround / preventForwardCollision）が拾って、空中に見えない壁と
//    床ができてしまう。
import {
  Group,
  Mesh,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  MeshBasicMaterial,
  CanvasTexture,
  DoubleSide,
  SRGBColorSpace,
} from 'three';
import { angleDiff } from './geo.js';

// 表示距離は輪の見かけの大きさから決めてある。半径250m の輪は 720p・縦画角60° で
// 30km 先だと直径 11px（点）、10km で 34px（はっきり見える）、3km で 114px。
// それより遠くに描いても見えないので出さない。遠距離の案内は HUD の方位矢印と
// 12km 圏の接近トーストが担う。
const SHOW_DIST_M = 30000;  // ここまで近づいたらシーンに入れる
const HIDE_DIST_M = 34000;  // これを超えたら外す（ヒステリシス）
const FADE_FAR_M = 28000;   // 距離フェード: これ以遠は透明
const FADE_NEAR_M = 10000;  // これ以内は満輝度
const MAX_ACTIVE = 10;      // 同時に表示する輪の最大数（画面が散らからないように）
const TRIGGER_SCAN_M = 6000; // 通過判定でスキャンする水平距離（半径250m・再武装750m に対し十分広い）
const EXIT_FACTOR = 3.0;    // 半径の何倍離れたら再武装するか
const YAW_DAMP = 1.2;       // /s ビルボード追従の指数レート
const TUBE_RATIO = 0.045;   // 半径に対するトーラス管の太さ（半径250m → 管11m）

const COLOR_NEW = 0xffc94d;     // 未訪問: 金色
const COLOR_VISITED = 0x7fe3b0; // 訪問済み: 緑

// 線分 P(=p0→p1) と点 S の最短距離の二乗。1フレームの移動でリングを飛び越えても
// 拾えるようにするための掃引判定。
function segPointDist2(p0x, p0y, p0z, p1x, p1y, p1z, sx, sy, sz) {
  const dx = p1x - p0x, dy = p1y - p0y, dz = p1z - p0z;
  const fx = p0x - sx, fy = p0y - sy, fz = p0z - sz;
  const dd = dx * dx + dy * dy + dz * dz;
  // dd === 0（静止・ポーズ復帰直後）で 0/0 = NaN になると比較がすり抜けるので守る。
  let t = dd > 0 ? -(fx * dx + fy * dy + fz * dz) / dd : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const gx = fx + t * dx, gy = fy + t * dy, gz = fz + t * dz;
  return gx * gx + gy * gy + gz * gz;
}

// 名所名を描いた板（Sprite）のテクスチャ。訪問済みになると内容が変わるので、
// canvas は使い回して描き直すだけにする（作り直すと 320KB のバッキングストアと
// GPU テクスチャを毎回捨てて確保することになる）。
function drawLabel(canvas, site, visited) {
  const ctx = canvas.getContext('2d');
  const accent = visited ? '#7fe3b0' : '#ffc94d';
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = 'rgba(8, 12, 24, 0.72)';
  ctx.strokeStyle = accent;
  ctx.lineWidth = 3;
  const r = 18;
  ctx.beginPath();
  ctx.roundRect(4, 4, 504, 152, r);
  ctx.fill();
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#eaf4ff';
  ctx.font = '600 52px -apple-system, "Hiragino Sans", "Yu Gothic UI", sans-serif';
  ctx.fillText((visited ? '✓ ' : '') + site.name, 256, 62, 470);
  ctx.fillStyle = '#a8b3d6';
  ctx.font = '400 28px -apple-system, sans-serif';
  ctx.fillText(site.nameEn, 256, 116, 470);
}

// enuSites は historicSites.js sitesToEnu() の戻り値（x/y/z/radiusM を持つ）。
// isVisited(id) は呼び出し側の進捗を参照するコールバック。
export function createHistoricRings({ scene, enuSites, isVisited, showLabels = true }) {
  // ジオメトリはインスタンス内で作る。モジュール直下に置くと、最初のゲームの
  // dispose() で破棄されて2回目以降の出発で使えなくなる（都市選び直しは通常操作）。
  const geometry = new TorusGeometry(1, TUBE_RATIO, 10, 72);

  // built に入っている輪は、そのままシーンに入っている輪でもある（近傍から外れたら
  // 破棄する）。activeList はその entry を近い順に並べたもの。
  const built = new Map();     // id → entry
  let activeList = [];         // 近い順の entry 配列
  let activeVersion = 0;       // 近傍セット/訪問状態が変わるたびに +1（ミニマップ同期用）
  const insideIds = new Set(); // 通過済みで、まだ十分離れていない id
  let elapsed = 0;
  let disposed = false;
  // 直近に近傍を評価した位置。Infinity 初期化で初回は必ず評価する。
  // ポーズ中は機体が動かないので、これで再評価そのものが止まる。
  let lastX = Infinity, lastY = Infinity, lastZ = Infinity;
  const REFRESH_MOVE_M = 50; // これだけ動いていなければ結果は変わらない

  function build(site) {
    const visited = !!isVisited(site.id);
    const group = new Group();
    group.name = `ring-${site.id}`;
    group.position.set(site.x, site.y, site.z);

    // マテリアルはリングごとに持つ（距離フェードで opacity を個別に動かすため）。
    const mesh = new Mesh(geometry, new MeshBasicMaterial({
      color: visited ? COLOR_VISITED : COLOR_NEW,
      transparent: true,
      opacity: 0,
      depthWrite: false, // 半透明どうしの描画順で穴が空かないように
      side: DoubleSide,
      fog: false,        // 霧に溶けず遠くからでも見える
    }));
    mesh.scale.setScalar(site.radiusM);
    mesh.renderOrder = 2;
    group.add(mesh);

    let sprite = null;
    if (showLabels) {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 160;
      drawLabel(canvas, site, visited);
      const texture = new CanvasTexture(canvas);
      texture.colorSpace = SRGBColorSpace;
      sprite = new Sprite(new SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        fog: false,
        opacity: 0,
      }));
      sprite.userData.canvas = canvas; // 訪問済みになったら描き直して使い回す
      sprite.scale.set(site.radiusM * 1.7, site.radiusM * 0.53, 1);
      sprite.position.y = site.radiusM * 1.35;
      sprite.renderOrder = 3;
      group.add(sprite);
    }

    const entry = { group, mesh, sprite, site, visited };
    built.set(site.id, entry);
    scene.add(group);
    return entry;
  }

  function destroy(entry) {
    scene.remove(entry.group);
    entry.mesh.material.dispose();
    entry.sprite?.material.map?.dispose();
    entry.sprite?.material.dispose();
    built.delete(entry.site.id);
  }

  // 近傍の名所を選び直す。毎フレームやる必要はない。
  // 距離は必ず 3D で測る。水平成分だけだと ENU の折り返し（中心角 90° 超で
  // R·sin が減少に転じる）で地球の裏側の名所が SHOW_DIST_M 圏内に見え、
  // y ≒ -12,700km の位置に不可視のリングを足してしまう。輪は地表 +350m なので、
  // 実際に近い名所については 3D と水平の差は無視できる。
  function refreshActive(ax, ay, az) {
    const next = [];
    for (const s of enuSites) {
      const dx = s.x - ax;
      const dy = s.y - ay;
      const dz = s.z - az;
      const sq = dx * dx + dy * dy + dz * dz;
      // すでに出ている輪は遠ざかるまで維持する（境界でのちらつき防止）。
      const limit = built.has(s.id) ? HIDE_DIST_M : SHOW_DIST_M;
      if (sq <= limit * limit) next.push({ s, sq });
    }
    next.sort((a, b) => a.sq - b.sq);
    if (next.length > MAX_ACTIVE) next.length = MAX_ACTIVE;

    // 顔ぶれが同じなら何もしない（移動していても大半のフレームがこれに当たる）。
    let changed = next.length !== built.size;
    if (!changed) {
      for (const { s } of next) if (!built.has(s.id)) { changed = true; break; }
    }
    if (!changed) return;

    const keep = new Set(next.map(({ s }) => s.id));
    for (const entry of [...built.values()]) {
      if (!keep.has(entry.site.id)) destroy(entry);
    }
    activeList = next.map(({ s }) => built.get(s.id) ?? build(s));
    activeVersion++;
  }

  return {
    // 機体がスポーンした位置で呼ぶ。すでに捕捉球の内側にある輪を「入場済み」として
    // 記録し、発火はさせない。出発地と名所の座標が一致していても（例: 広島＝原爆
    // ドーム）離陸前にモーダルが開かない、という保証をここが担う。輪の高度と
    // スポーン高度の数値的な余裕に頼らずに済む。
    arm(pos) {
      if (disposed) return;
      for (const s of enuSites) {
        const dx = s.x - pos.x, dy = s.y - pos.y, dz = s.z - pos.z;
        if (dx * dx + dy * dy + dz * dz <= s.radiusM * s.radiusM) insideIds.add(s.id);
      }
    },

    // 毎フレーム呼ぶ（ポーズ中も可 — 脈動が続いて凍結画面が生きて見える）。
    // 近傍の再評価だけ 8 フレームに1回、かつ一定距離動いたときだけ。位相 2 は
    // minimap 側の (frameCounter & 3) === 0 と決して重ならない
    // （4 だと 4&3 === 0 で毎回同じフレームに処理が集中してしまう）。
    update(dt, aircraftPos, frameCounter) {
      if (disposed) return;
      elapsed += dt;
      const ax = aircraftPos.x, ay = aircraftPos.y, az = aircraftPos.z;
      if ((frameCounter & 7) === 2) {
        const mx = ax - lastX, my = ay - lastY, mz = az - lastZ;
        if (mx * mx + my * my + mz * mz >= REFRESH_MOVE_M * REFRESH_MOVE_M) {
          lastX = ax; lastY = ay; lastZ = az;
          refreshActive(ax, ay, az);
        }
      }

      const pulse = 0.5 + 0.5 * Math.sin(elapsed * 3.4);
      const yawEase = 1 - Math.exp(-YAW_DAMP * dt); // dt にしか依らないのでループ外
      for (const entry of activeList) {
        const s = entry.site;

        // Y軸ビルボード: トーラスの穴の向き（ローカル +Z）を機体へ向ける。
        const target = Math.atan2(ax - s.x, az - s.z);
        entry.group.rotation.y += angleDiff(target, entry.group.rotation.y) * yawEase;

        // 距離フェード（fog:false なので自前でやる）。
        const dx = s.x - ax, dz = s.z - az;
        const dist = Math.sqrt(dx * dx + dz * dz);
        let fade = (FADE_FAR_M - dist) / (FADE_FAR_M - FADE_NEAR_M);
        fade = fade < 0 ? 0 : fade > 1 ? 1 : fade;

        const base = entry.visited ? 0.45 : 0.7 + 0.3 * pulse;
        entry.mesh.material.opacity = base * fade;
        if (entry.sprite) entry.sprite.material.opacity = fade;
        if (!entry.visited) entry.mesh.scale.setScalar(s.radiusM * (1 + 0.025 * pulse));
      }
    },

    // 1フレームの移動線分 prev→curr で「輪をくぐった」名所を返す（無ければ null）。
    // ポーズ中は呼ばないこと。
    checkTrigger(prev, curr) {
      if (disposed) return null;
      let fired = null;
      let firedD2 = Infinity;
      const scan2 = TRIGGER_SCAN_M * TRIGGER_SCAN_M;
      for (const s of enuSites) {
        const hx = s.x - curr.x;
        const hz = s.z - curr.z;
        if (hx * hx + hz * hz > scan2) continue; // 遠すぎる名所は触らない
        const r = s.radiusM;
        const d2 = segPointDist2(
          prev.x, prev.y, prev.z,
          curr.x, curr.y, curr.z,
          s.x, s.y, s.z
        );
        if (d2 <= r * r) {
          // 同一フレームで複数の輪に入りうる（近接する遺跡群）。全部 inside に
          // 入れておかないと、モーダルを閉じた直後に次の輪が即発火してしまう。
          if (!insideIds.has(s.id)) {
            insideIds.add(s.id);
            if (d2 < firedD2) { firedD2 = d2; fired = s; } // 発火は最も近い1件だけ
          }
        } else if (insideIds.has(s.id)) {
          const ex = r * EXIT_FACTOR;
          const dy = curr.y - s.y;
          if (hx * hx + dy * dy + hz * hz > ex * ex) insideIds.delete(s.id);
        }
      }
      return fired;
    },

    // 訪問済みになった輪の見た目を切り替える。まだ生成されていない輪でも
    // ミニマップの色は追従させたいので、バージョンは早期 return より前に上げる。
    markVisited(id) {
      activeVersion++;
      const entry = built.get(id);
      if (!entry || entry.visited) return;
      entry.visited = true;
      entry.mesh.material.color.setHex(COLOR_VISITED);
      entry.mesh.scale.setScalar(entry.site.radiusM);
      if (entry.sprite) {
        // canvas は使い回し、テクスチャは再アップロードするだけ。
        drawLabel(entry.sprite.userData.canvas, entry.site, true);
        entry.sprite.material.map.needsUpdate = true;
      }
    },

    // ミニマップ同期用。近い順の名所配列と、変化検出用のバージョン。
    getActive() { return activeList.map((e) => e.site); },
    getActiveVersion() { return activeVersion; },

    dispose() {
      disposed = true;
      for (const entry of [...built.values()]) destroy(entry);
      built.clear();
      insideIds.clear();
      activeList = [];
      geometry.dispose();
    },
  };
}
